import React, { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ClubbieButton from "../../components/ClubbieButton/ClubbieButton";
import ClubbieDropdown from "../../components/ClubbieDropdown/ClubbieDropdown";
import { updateTournament } from "../../redux/slices/tournamentSlice";
import { DASHBOARD_STACK } from "../../utils/screenNames";
import { tournamentWizardStepCompleted } from "../../utils/analytics";
import { WIZARD_STEP } from "../../utils/analyticsEvents";
import "./SetMatchRules.css";

const DEFAULT_RULES = {
  gameDuration: "30",
  breaks: "1",
  breakDuration: "5",
  intervalBetweenMatches: "5",
  pointsForWin: "0",
  pointsForDraw: "0",
  pointsForLoss: "0",
};

const DROPDOWN_OPTIONS = {
  gameDuration: ["10", "15", "20", "30", "40", "45", "60", "90"],
  breaks: ["1", "2", "3", "4"],
  breakDuration: ["5", "10", "15", "20", "25", "30", "45", "60"],
  intervalBetweenMatches: [
    "5",
    "10",
    "15",
    "20",
    "25",
    "30",
    "45",
    "60",
    "90",
    "120",
  ],
  pointsForWin: ["1", "2", "3", "4", "5"],
  pointsForDraw: ["0", "1", "2", "3"],
  pointsForLoss: ["0", "1", "2", "3", "4", "5"],
};

const SetMatchRules = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedTournament = useSelector(
    (state) => state.tournament.selectedTournament
  );

  const getInitialMatchRules = useCallback(() => {
    if (!selectedTournament?.matchRules) return DEFAULT_RULES;

    const {
      gameDuration,
      breaks,
      breakDuration,
      intervalBetweenMatches,
      pointsForWin,
      pointsForDraw,
      pointsForLoss,
    } = selectedTournament.matchRules;

    return {
      gameDuration: gameDuration?.toString() || DEFAULT_RULES.gameDuration,
      breaks: breaks?.toString() || DEFAULT_RULES.breaks,
      breakDuration: breakDuration?.toString() || DEFAULT_RULES.breakDuration,
      intervalBetweenMatches:
        intervalBetweenMatches?.toString() ||
        DEFAULT_RULES.intervalBetweenMatches,
      pointsForWin: pointsForWin?.toString() || DEFAULT_RULES.pointsForWin,
      pointsForDraw: pointsForDraw?.toString() || DEFAULT_RULES.pointsForDraw,
      pointsForLoss: pointsForLoss?.toString() || DEFAULT_RULES.pointsForLoss,
    };
  }, [selectedTournament]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [matchRules, setMatchRules] = useState(getInitialMatchRules());
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = useCallback((field, value) => {
    setMatchRules((prev) => ({ ...prev, [field]: value }));
    setTimeout(() => setOpenDropdown(null), 100);
  }, []);

  const validateRules = useCallback(() => {
    const newErrors = Object.entries(matchRules).reduce((acc, [key, value]) => {
      if (!value)
        acc[key] =
          `${key.replace(/([A-Z])/g, " $1").toLowerCase()} is required`;
      return acc;
    }, {});

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [matchRules]);

  const handleNext = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!validateRules()) {
        toast.error("Please fill in all required fields");
        return;
      }

      if (selectedTournament?.id) {
        const updateData = Object.fromEntries(
          Object.entries(matchRules).map(([key, value]) => [
            key,
            parseInt(value),
          ])
        );

        const result = await dispatch(
          updateTournament({
            id: selectedTournament.id,
            updateData: { matchRules: updateData },
          })
        );

        if (result.meta.requestStatus === "fulfilled") {
          tournamentWizardStepCompleted({
            stepName: WIZARD_STEP.SET_MATCH_RULES,
            tournamentId: selectedTournament?.id,
          });
          toast.success("Match rules updated successfully!");
          navigate(DASHBOARD_STACK.CREATE_TOURNAMENT.SET_MATCH_SCHEDULE);
        } else {
          throw new Error("Failed to update match rules");
        }
      }
    } catch (error) {
      toast.error(error.message || "Failed to update match rules");
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, matchRules, navigate, selectedTournament, validateRules]);

  const dropdownFields = useMemo(
    () => [
      { name: "gameDuration", label: "How long is a game?", suffix: "minutes" },
      {
        name: "breaks",
        label: "How many breaks?",
      },
      {
        name: "breakDuration",
        label: "How long is the break duration?",
        suffix: "minutes",
      },
      {
        name: "intervalBetweenMatches",
        label: "How long is the interval between matches?",
        suffix: "minutes",
      },

      { name: "pointsForWin", label: "How many points for a win?" },
      { name: "pointsForDraw", label: "How many points for a draw?" },
      { name: "pointsForLoss", label: "How many points for a loss?" },
    ],
    []
  );

  return (
    <>
      <div className="input-section">
        <p>Set Match Rules</p>
        <div className="rules-container">
          {dropdownFields.map(({ name, label, suffix }) => (
            <ClubbieDropdown
              key={name}
              name={name}
              label={label}
              value={matchRules[name]}
              options={DROPDOWN_OPTIONS[name]}
              onChange={(value) => handleChange(name, value)}
              error={errors[name]}
              suffix={suffix}
              required
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
            />
          ))}
        </div>
      </div>

      <ClubbieButton
        className="next-button"
        onClick={handleNext}
        isLoading={isLoading}
      >
        Next
      </ClubbieButton>
    </>
  );
};

export default SetMatchRules;
